import React, { useState } from "react";
import Logo from "../../assets/rxultimate.png";
import SignupImage from "../../assets/3836182.jpg";
import axios from "axios";
import { api_url } from "../../utils/data";
import { toast } from "react-toastify";
import { Link, Navigate } from "react-router-dom";
import { RotatingLines } from "react-loader-spinner";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useAuthenticate } from "../../context/AuthContext";

const Signup = () => {
  const { user } = useAuthenticate();
  const [showPassword, setShowPassword] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [signupData, setSignupData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    username: "",
    password: "",
    pharmacy_name: "",
    location: "",
    pharmacy_phone: "",
    pharmacy_email: "",
    pharmacy_logo: null
  });

  const handleFormChange = (e) => {
    setSignupData({
      ...signupData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileChange = (e) => {
    setSignupData({
      ...signupData,
      pharmacy_logo: e.target.files[0]
    });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const nextStep = (e) => {
    e.preventDefault();
    setCurrentStep(2);
  };

  const prevStep = (e) => {
    e.preventDefault();
    setCurrentStep(1);
  };

  const registerUser = (e) => {
    e.preventDefault();
    setIsRegistering(true);
    
    const formData = new FormData();
    
    // Add all form fields to FormData
    Object.keys(signupData).forEach(key => {
        if (signupData[key] !== null) {
            if (key === 'pharmacy_logo' && signupData[key]) {
                formData.append(key, signupData[key]);
            } else if (key !== 'pharmacy_logo') {
                formData.append(key, signupData[key]);
            }
        }
    });

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    };

    axios
      .post(`${api_url}/accounts/register/`, formData, config)
      .then((response) => {
        toast.success("Registration successful. Please login.");
        setIsRegistering(false);
        window.location.href = "/login";
      })
      .catch((error) => {
        setIsRegistering(false);
        if(error?.response?.status !== 400){
            toast.error("Something unexpected happened. Please try again later");
            return ;
        }
        if (error?.response?.data) {
          for (const property in error.response.data) {
            if (property !== "detail") {
              toast.error(`${property}: ${error.response.data[property]}`);
            } else {
              toast.error(`${error.response.data[property]}`);
            }
          }
        }
      });
  };

  if (user) {
    if (user?.is_superuser) {
      return <Navigate to="/super-admin/dashboard" />;
    } else {
      return <Navigate to="/dashboard" />;
    }
  } else {
    return (
      <div className="min-h-screen w-full bg-white">
        <div className="flex flex-col md:flex-row h-full min-h-screen">
          {/* Form Section */}
          <div className="w-full md:w-1/2 flex items-center justify-center p-4 md:p-8 lg:p-12">
            <div className="w-full max-w-md">
              <form onSubmit={currentStep === 1 ? nextStep : registerUser} className="w-full">
                <div className="mb-8">
                  <div className="flex justify-center md:justify-start mb-8">
                    <img
                      src={Logo}
                      alt="rxultimate"
                      className="h-16 md:h-20 w-auto"
                    />
                  </div>
                  <div className="">
                    <h3 className="text-xl md:text-2xl font-semibold text-[#526484] mb-2">
                      Create Account
                    </h3>
                    <p className="text-[#526484] text-sm">
                      {currentStep === 1 
                        ? "Sign up for Rxultimate by providing your personal details."
                        : "Complete your registration with pharmacy information."}
                    </p>
                  </div>
                </div>

                {currentStep === 1 ? (
                  // Step 1: Personal Details
                  <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col">
                        <label
                          className="text-[#344357] font-medium text-sm mb-1.5"
                          htmlFor="first_name"
                        >
                          First Name*
                        </label>
                        <input
                          type="text"
                          className="py-3 px-4 text-base rounded-lg border border-[#dbdfea] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          name="first_name"
                          id="first_name"
                          placeholder="Enter first name"
                          value={signupData.first_name}
                          onChange={handleFormChange}
                          required
                        />
                      </div>
                      <div className="flex flex-col">
                        <label
                          className="text-[#344357] font-medium text-sm mb-1.5"
                          htmlFor="last_name"
                        >
                          Last Name*
                        </label>
                        <input
                          type="text"
                          className="py-3 px-4 text-base rounded-lg border border-[#dbdfea] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          name="last_name"
                          id="last_name"
                          placeholder="Enter last name"
                          value={signupData.last_name}
                          onChange={handleFormChange}
                          required
                        />
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <label
                        className="text-[#344357] font-medium text-sm mb-1.5"
                        htmlFor="email"
                      >
                        Email Address*
                      </label>
                      <input
                        type="email"
                        className="py-3 px-4 text-base rounded-lg border border-[#dbdfea] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        name="email"
                        id="email"
                        placeholder="Enter your email"
                        value={signupData.email}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    <div className="flex flex-col">
                      <label
                        className="text-[#344357] font-medium text-sm mb-1.5"
                        htmlFor="phone_number"
                      >
                        Phone Number*
                      </label>
                      <input
                        type="tel"
                        className="py-3 px-4 text-base rounded-lg border border-[#dbdfea] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        name="phone_number"
                        id="phone_number"
                        placeholder="Enter phone number"
                        value={signupData.phone_number}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    <div className="flex flex-col">
                      <label
                        className="text-[#344357] font-medium text-sm mb-1.5"
                        htmlFor="username"
                      >
                        Username*
                      </label>
                      <input
                        type="text"
                        className="py-3 px-4 text-base rounded-lg border border-[#dbdfea] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        name="username"
                        id="username"
                        placeholder="Choose a username"
                        value={signupData.username}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    <div className="flex flex-col">
                      <label
                        className="text-[#344357] font-medium text-sm mb-1.5"
                        htmlFor="password"
                      >
                        Password*
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          className="w-full py-3 px-4 text-base rounded-lg border border-[#dbdfea] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                          name="password"
                          id="password"
                          placeholder="Create a password"
                          value={signupData.password}
                          onChange={handleFormChange}
                          required
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          onClick={togglePasswordVisibility}
                        >
                          {showPassword ? (
                            <FaEyeSlash className="text-lg" />
                          ) : (
                            <FaEye className="text-lg" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="pt-3">
                      <button
                        type="submit"
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                      >
                        Next: Pharmacy Details
                      </button>
                      <div className="text-center mt-4">
                        <p className="text-[#526484] text-sm">
                          Already have an account?{" "}
                          <Link
                            to="/login"
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Login
                          </Link>
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Step 2: Pharmacy Details
                  <div className="space-y-5">
                    <div className="flex flex-col">
                      <label
                        className="text-[#344357] font-medium text-sm mb-1.5"
                        htmlFor="pharmacy_name"
                      >
                        Pharmacy Name*
                      </label>
                      <input
                        type="text"
                        className="py-3 px-4 text-base rounded-lg border border-[#dbdfea] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        name="pharmacy_name"
                        id="pharmacy_name"
                        placeholder="Enter pharmacy name"
                        value={signupData.pharmacy_name}
                        onChange={handleFormChange}
                        required
                      />
                    </div>

                    <div className="flex flex-col">
                      <label
                        className="text-[#344357] font-medium text-sm mb-1.5"
                        htmlFor="location"
                      >
                        Location
                      </label>
                      <input
                        type="text"
                        className="py-3 px-4 text-base rounded-lg border border-[#dbdfea] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        name="location"
                        id="location"
                        placeholder="Enter pharmacy location"
                        value={signupData.location}
                        onChange={handleFormChange}
                      />
                    </div>

                    <div className="flex flex-col">
                      <label
                        className="text-[#344357] font-medium text-sm mb-1.5"
                        htmlFor="pharmacy_phone"
                      >
                        Pharmacy Phone Number
                      </label>
                      <input
                        type="tel"
                        className="py-3 px-4 text-base rounded-lg border border-[#dbdfea] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        name="pharmacy_phone"
                        id="pharmacy_phone"
                        placeholder="Enter pharmacy phone"
                        value={signupData.pharmacy_phone}
                        onChange={handleFormChange}
                      />
                    </div>

                    <div className="flex flex-col">
                      <label
                        className="text-[#344357] font-medium text-sm mb-1.5"
                        htmlFor="pharmacy_email"
                      >
                        Pharmacy Email Address
                      </label>
                      <input
                        type="email"
                        className="py-3 px-4 text-base rounded-lg border border-[#dbdfea] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        name="pharmacy_email"
                        id="pharmacy_email"
                        placeholder="Enter pharmacy email"
                        value={signupData.pharmacy_email}
                        onChange={handleFormChange}
                      />
                    </div>

                    <div className="flex flex-col">
                      <label
                        className="text-[#344357] font-medium text-sm mb-1.5"
                        htmlFor="pharmacy_logo"
                      >
                        Pharmacy Logo
                      </label>
                      <input
                        type="file"
                        className="py-2.5 px-4 text-base rounded-lg border border-[#dbdfea] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        name="pharmacy_logo"
                        id="pharmacy_logo"
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                    </div>

                    <div className="pt-3 flex gap-4">
                      <button
                        type="button"
                        onClick={prevStep}
                        className="w-1/3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-3 px-4 rounded-lg transition-colors"
                      >
                        Back
                      </button>
                      {isRegistering ? (
                        <div className="flex justify-center items-center w-2/3 py-2">
                          <RotatingLines
                            visible={true}
                            height="48"
                            width="48"
                            strokeColor="blue"
                            strokeWidth="5"
                            animationDuration="0.75"
                            ariaLabel="rotating-lines-loading"
                          />
                        </div>
                      ) : (
                        <button
                          type="submit"
                          className="w-2/3 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                        >
                          Create Account
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* Image Section - Hidden on small screens */}
          <div className="hidden md:flex md:w-1/2 bg-[#f5f6fa] items-center justify-center">
            <img
              src={SignupImage}
              alt="Signup illustration"
              className="w-4/5 max-w-lg object-contain"
            />
          </div>
        </div>
      </div>
    );
  }
};

export default Signup;